type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

const FAILURE_THRESHOLD = 3;
const COOLDOWN_MS = 10000; // 10 seconds

let state: CircuitState = 'CLOSED';
let failureCount = 0;
let openedAt = 0;

export function getCircuitState(): CircuitState {
  if (state === 'OPEN' && Date.now() - openedAt >= COOLDOWN_MS) {
    state = 'HALF_OPEN';
  }
  return state;
}

export function getRetryCountdown(): number {
  if (state !== 'OPEN') return 0;
  const elapsed = Date.now() - openedAt;
  return Math.max(0, Math.ceil((COOLDOWN_MS - elapsed) / 1000));
}

export function recordSuccess(): void {
  failureCount = 0;
  state = 'CLOSED';
}

export function recordFailure(): void {
  failureCount++;
  if (failureCount >= FAILURE_THRESHOLD) {
    state = 'OPEN';
    openedAt = Date.now();
  }
}

export function resetCircuitBreaker(): void {
  state = 'CLOSED';
  failureCount = 0;
  openedAt = 0;
}

export function checkCircuitBreaker(): void {
  const currentState = getCircuitState();
  if (currentState === 'OPEN') {
    const countdown = getRetryCountdown();
    throw new Error(`Network temporarily unavailable. Trying again in ${countdown} seconds.`);
  }
}

export async function withRetry<T>(fn: () => Promise<T>, maxRetries: number = 3): Promise<T> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      checkCircuitBreaker();
      const result = await fn();
      recordSuccess();
      return result;
    } catch (err) {
      lastError = err as Error;

      // Don't retry circuit breaker errors
      if (/temporarily unavailable/i.test(lastError.message)) {
        throw lastError;
      }

      // Check if this is a network error worth retrying
      const isNetworkError = /UNAVAILABLE|ECONNREFUSED|DEADLINE_EXCEEDED|connection refused/i.test(
        lastError.message,
      );

      if (isNetworkError) {
        recordFailure();
      }

      if (attempt < maxRetries && isNetworkError) {
        const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        throw lastError;
      }
    }
  }

  throw lastError;
}
